import React, { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { ArrowRight, Clock, ListChecks, MessageCircleHeart, ShieldCheck } from 'lucide-react';
import { useLanguage } from '../../assets/js/LanguageContext';
import SectionEyebrow from '../common/SectionEyebrow';
import Loader from '../common/Loader';
import '../../assets/styles/SurveyAnswer.css';

const API = process.env.REACT_APP_API_URL;

// Tiempo aproximado: las abiertas llevan mas (escribir) que elegir una opcion o puntuar
const estimateMinutes = (questions) => {
  const seconds = questions.reduce((sum, q) => sum + (q.tipo === 'open' ? 60 : 15), 0);
  return Math.max(1, Math.round(seconds / 60));
};

// Paso entre el PIN y las preguntas: dice que diagnostico es, cuanto se tarda y que es anonimo.
const Welcome = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const pin = localStorage.getItem('pin');
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!pin) return undefined;
    let cancelled = false;
    fetch(`${API}/public/survey/${pin}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => { if (!cancelled) setSurvey(data); })
      .catch(() => { if (!cancelled) setSurvey(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [pin]);

  if (!pin) return <Navigate to="/pin" replace />;

  const questions = Array.isArray(survey && survey.questions) ? survey.questions : [];
  const minutes = estimateMinutes(questions);

  const points = [
    { icon: <ShieldCheck size={20} strokeWidth={1.75} />, title: t('welcome_anonymous_title'), text: t('welcome_anonymous_text') },
    {
      icon: <Clock size={20} strokeWidth={1.75} />,
      title: questions.length
        ? t(minutes === 1 ? 'welcome_time_title_one' : 'welcome_time_title').replace('{minutes}', minutes)
        : t('welcome_time_title_short'),
      text: t('welcome_time_text')
    },
    { icon: <MessageCircleHeart size={20} strokeWidth={1.75} />, title: t('welcome_honest_title'), text: t('welcome_honest_text') }
  ];

  return (
    <main className="tp-answer tp-welcome">
      <div className="tp-answer__inner">
        {loading ? (
          <div className="tp-answer__inner--center"><Loader /></div>
        ) : (
          <>
            <header className="tp-answer__head">
              <SectionEyebrow>{t('welcome_eyebrow')}</SectionEyebrow>
              <h1 className="tp-answer__title">{(survey && survey.surveyName) || t('welcome_title_fallback')}</h1>
              <p className="tp-welcome__lead">{t('welcome_lead')}</p>
            </header>

            <section className="tp-answer__card">
              {questions.length > 0 && (
                <p className="tp-welcome__meta">
                  <ListChecks size={16} strokeWidth={2} aria-hidden="true" />
                  {t(questions.length === 1 ? 'welcome_questions_one' : 'welcome_questions').replace('{count}', questions.length)}
                  <span className="tp-welcome__pin">PIN {pin}</span>
                </p>
              )}

              <ul className="tp-welcome__points">
                {points.map((point) => (
                  <li key={point.title} className="tp-welcome__point">
                    <span className="tp-welcome__icon" aria-hidden="true">{point.icon}</span>
                    <span>
                      <strong>{point.title}</strong>
                      <span>{point.text}</span>
                    </span>
                  </li>
                ))}
              </ul>

              <div className="tp-welcome__actions">
                <Link to="/pin" className="tp-welcome__other">{t('welcome_other_pin')}</Link>
                <button type="button" className="btn btn--primary" onClick={() => navigate(`/${pin}`)}>
                  {t('welcome_start')}
                  <ArrowRight size={16} strokeWidth={2.25} aria-hidden="true" />
                </button>
              </div>
            </section>
          </>
        )}
      </div>
    </main>
  );
};

export default Welcome;
